import React, { useState } from 'react';
import { Plus, Link as LinkIcon, FileUp, X } from 'lucide-react';
import { parse } from 'iptv-playlist-parser';

interface PlaylistManagerProps {
  onPlaylistAdded: (channels: any[], name: string) => void;
}

export const PlaylistManager: React.FC<PlaylistManagerProps> = ({ onPlaylistAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch playlist');
      const text = await response.text();
      const playlist = parse(text);
      onPlaylistAdded(playlist.items, url.split('/').pop() || 'Remote Playlist');
      setUrl('');
      setIsOpen(false);
    } catch (err) {
      setError('Could not load playlist. Check the URL and CORS settings.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError('');
    try {
      const text = await file.text();
      const playlist = parse(text);
      onPlaylistAdded(playlist.items, file.name);
      setIsOpen(false);
    } catch (err) {
      setError('Could not parse file. Ensure it is a valid M3U playlist.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors w-full justify-center"
      >
        <Plus size={18} />
        <span>Add Playlist</span>
      </button>
    );
  }

  return (
    <div className="p-4 bg-gray-800 rounded-xl border border-gray-700 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold">Add New Playlist</h3>
        <button 
          onClick={() => setIsOpen(false)} 
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <form onSubmit={handleUrlSubmit} className="space-y-3">
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="url"
            placeholder="M3U URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <button
          disabled={isLoading}
          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Loading...' : 'Add from URL'}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-gray-800 px-2 text-gray-400">Or</span>
        </div>
      </div>

      <label className="flex items-center justify-center gap-2 w-full py-2 border border-dashed border-gray-600 hover:border-indigo-500 text-gray-400 hover:text-indigo-400 rounded-lg cursor-pointer transition-all text-sm">
        <FileUp size={18} />
        <span>Upload M3U File</span>
        <input type="file" accept=".m3u,.m3u8" onChange={handleFileUpload} className="hidden" />
      </label>

      {error && <p className="text-red-400 text-xs text-center">{error}</p>}
    </div>
  );
};
